import { forwardRef, type CSSProperties } from 'react';
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  ImageIcon,
  Loader2,
  RefreshCw,
  X,
} from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import { cn } from '@/lib/utils';
import type { MultiImageUploadSlot } from './multi-image-upload-types';

/**
 * A single slot in the MultiImageUpload grid. Stateless — every
 * behavior hangs off the parent via callbacks so the parent is the
 * single source of truth for ordering, status, and validation.
 *
 * Visual states per design §8.7 + the states sheet at
 * `.dukanti/.stitch/designs/multi-image-upload-states.html`:
 *  - `idle`      : thumbnail, drag handle (desktop) or reorder
 *                  buttons (mobile), remove (X) button
 *  - `uploading` : thumbnail dimmed, spinner overlay
 *  - `failed`    : thumbnail with terracotta overlay + retry/remove
 *                  CTAs and a caption below
 *
 * `@dnd-kit` attributes / listeners / transform are forwarded via
 * props — the parent uses `useSortable` and hands them here, so the
 * slot itself has no dnd-kit import (cleaner test surface).
 */
export interface MultiImageUploadSlotProps {
  slot: MultiImageUploadSlot;
  index: number;
  /** True when the up-button should be disabled. */
  canMoveUp: boolean;
  /** True when the down-button should be disabled. */
  canMoveDown: boolean;
  /** Disable all interactions (form submitting etc.). */
  disabled?: boolean;
  onRemove: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onRetry?: (index: number) => void;
  /** data-testid prefix, e.g. `"product-image-upload-slot"`. */
  testIdPrefix: string;
  /** dnd-kit listeners — bound to the drag handle. */
  dragListeners?: Record<string, (event: unknown) => void>;
  /** dnd-kit attributes — bound to the drag handle. */
  dragAttributes?: Record<string, unknown>;
  /** Transform style from dnd-kit; the parent computes it. */
  style?: CSSProperties;
  /** True while dnd-kit reports this slot as being dragged. */
  isDragging?: boolean;
}

export const MultiImageUploadSlotView = forwardRef<HTMLDivElement, MultiImageUploadSlotProps>(
  function MultiImageUploadSlotView(props, ref) {
    const {
      slot,
      index,
      canMoveUp,
      canMoveDown,
      disabled,
      onRemove,
      onMoveUp,
      onMoveDown,
      onRetry,
      testIdPrefix,
      dragListeners,
      dragAttributes,
      style,
      isDragging,
    } = props;
    const { t } = useTranslation();

    const status = slot.status ?? 'idle';
    const idSuffix = slot.key;
    const baseTestId = `${testIdPrefix}-${index}`;

    return (
      <div
        ref={ref}
        style={style}
        className={cn(
          'bg-sand-100 rounded-card ring-sand-200 group relative aspect-square overflow-hidden ring-1',
          status === 'failed' && 'ring-terracotta ring-2',
          status === 'uploading' && 'ring-zaatar-soft ring-2',
          isDragging && 'shadow-card-elevated z-10 opacity-80',
        )}
        data-testid={baseTestId}
        data-slot-status={status}
      >
        <img
          src={slot.previewUrl}
          alt={t('admin.products.form.imageSlotAlt', { index: index + 1 })}
          className="h-full w-full object-cover"
          // Object URLs fire onload → the parent's useEffect manages
          // revocation when the slot is removed.
          draggable={false}
        />

        {/* --- Remove button (X, top-end) ----------------------------- */}
        <button
          type="button"
          aria-label={t('admin.products.form.imageRemove')}
          onClick={() => onRemove(index)}
          disabled={disabled || status === 'uploading'}
          className={cn(
            'bg-sand-900/70 hover:bg-sand-900/90 absolute end-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full text-white transition-colors',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
          data-testid={`${baseTestId}-remove`}
        >
          <X size={12} strokeWidth={2.5} aria-hidden />
        </button>

        {/* --- Drag handle (desktop only, hidden on touch) ------------
          Positioned at the TOP of the card so it doesn't overlap the
          reorder-buttons row at the bottom (which is always rendered).
          Centered horizontally via a full-width row + inline-flex —
          the pattern is direction-agnostic because `justify-center`
          works the same in LTR and RTL. */}
        <div className="pointer-events-none absolute inset-x-0 top-1.5 hidden justify-center sm:flex">
          <button
            type="button"
            aria-label={t('admin.products.form.imageDragHandle')}
            className={cn(
              'bg-paper/80 text-sand-500 hover:text-zaatar pointer-events-auto flex h-7 w-7 items-center justify-center rounded-md transition-colors',
              // dnd-kit semantic: cursor-grab becomes cursor-grabbing
              // when the drag sensor fires, via @dnd-kit's CSS pseudo.
              'focus-visible:ring-zaatar cursor-grab focus-visible:outline-none focus-visible:ring-2',
            )}
            data-testid={`${baseTestId}-drag`}
            {...dragAttributes}
            {...dragListeners}
          >
            <GripVertical size={14} aria-hidden />
          </button>
        </div>

        {/* --- Reorder buttons (all breakpoints) --------------------
          Mobile users rely on these as the primary affordance; desktop
          keyboard/mouse users get them as a secondary path alongside
          drag. Always-visible keeps the a11y tree uniform and lets
          E2E tests target one set of selectors regardless of viewport.
          Same center-via-row trick — layout is direction-neutral. */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-1.5 flex justify-center gap-1"
          aria-hidden={false}
        >
          <button
            type="button"
            aria-label={t('admin.products.form.imageMoveUp')}
            onClick={() => onMoveUp(index)}
            disabled={disabled || !canMoveUp || status === 'uploading'}
            className={cn(
              'bg-sand-100 text-zaatar pointer-events-auto flex h-7 w-7 items-center justify-center rounded-md',
              'disabled:cursor-not-allowed disabled:opacity-40',
            )}
            data-testid={`${baseTestId}-move-up`}
          >
            <ChevronUp size={14} strokeWidth={2} aria-hidden />
          </button>
          <button
            type="button"
            aria-label={t('admin.products.form.imageMoveDown')}
            onClick={() => onMoveDown(index)}
            disabled={disabled || !canMoveDown || status === 'uploading'}
            className={cn(
              'bg-sand-100 text-zaatar pointer-events-auto flex h-7 w-7 items-center justify-center rounded-md',
              'disabled:cursor-not-allowed disabled:opacity-40',
            )}
            data-testid={`${baseTestId}-move-down`}
          >
            <ChevronDown size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>

        {/* --- Uploading overlay ------------------------------------- */}
        {status === 'uploading' ? (
          <div
            className="bg-paper/60 absolute inset-0 flex items-center justify-center"
            data-testid={`${baseTestId}-uploading`}
            role="status"
            aria-live="polite"
          >
            <span className="sr-only">{t('admin.products.form.imageUploading')}</span>
            <Loader2 className="text-zaatar h-6 w-6 animate-spin" aria-hidden />
          </div>
        ) : null}

        {/* --- Failed overlay ---------------------------------------- */}
        {status === 'failed' ? (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[rgba(184,71,47,0.15)]"
            data-testid={`${baseTestId}-failed`}
            role="alert"
          >
            {onRetry ? (
              <button
                type="button"
                onClick={() => onRetry(index)}
                disabled={disabled}
                aria-label={t('admin.products.form.imageRetry')}
                className="bg-terracotta flex h-8 w-8 items-center justify-center rounded-md text-white disabled:opacity-50"
                data-testid={`${baseTestId}-retry`}
              >
                <RefreshCw size={16} strokeWidth={2} aria-hidden />
              </button>
            ) : null}
          </div>
        ) : null}

        {/* --- Fallback icon when image src is empty ----------------- */}
        {!slot.previewUrl ? (
          <div className="text-sand-400 absolute inset-0 flex items-center justify-center">
            <ImageIcon size={32} strokeWidth={1.5} aria-hidden />
          </div>
        ) : null}

        {/* Screen-reader summary of position for keyboard users */}
        <span className="sr-only" id={`slot-pos-${idSuffix}`}>
          {t('admin.products.form.imagePosition', {
            current: index + 1,
          })}
        </span>
      </div>
    );
  },
);
